import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, ProgressBar, Form, Badge } from 'react-bootstrap';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { auth } from '../../firebase';
import Icon from '../UI/Icon';
import { saveStepCompletion, getUserModuleProgress, removeStepCompletion } from '../../services/moduleProgressService';

const COLLECTIONS = {
  USER_PROGRESS: 'user_progress'
};

function ModuleView({ moduleId, moduleData, onBack, onModuleComplete, onNavigateToGame, onNavigateToMaterials, onNavigateToNextModule }) {
  const [completedSteps, setCompletedSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [checklistItems, setChecklistItems] = useState({});
  const [fetchedModuleData, setFetchedModuleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [learningMaterials, setLearningMaterials] = useState(new Map());
  const [materialsLoaded, setMaterialsLoaded] = useState(false);

  // First, fetch learning materials
  useEffect(() => {
    fetchLearningMaterials();
  }, []); // Only run once on mount

  // Then, fetch module data if not provided
  useEffect(() => {
    if (!moduleData && moduleId) {
      fetchModuleData();
    }
  }, [moduleId, moduleData]);

  // Load completed steps and checklist from localStorage on module load
  useEffect(() => {
    if (moduleId) {
      loadCompletedSteps();
      loadChecklistItems();
    }
  }, [moduleId]);

  const loadCompletedSteps = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        // Load from Firebase
        const firebaseProgress = await getUserModuleProgress(user.uid, moduleId);
        setCompletedSteps(firebaseProgress);
        
        // Also save to localStorage for offline access
        localStorage.setItem(`completedSteps_module_${moduleId}`, JSON.stringify(firebaseProgress));
      } else {
        // Fallback to localStorage if no user
        const saved = localStorage.getItem(`completedSteps_module_${moduleId}`);
        if (saved) {
          const completedArray = JSON.parse(saved);
          setCompletedSteps(completedArray);
        }
      }
    } catch (error) {
      console.error('Error loading completed steps:', error);
      // Fallback to localStorage on error
      const saved = localStorage.getItem(`completedSteps_module_${moduleId}`);
      if (saved) {
        const completedArray = JSON.parse(saved);
        setCompletedSteps(completedArray);
      }
    }
  };

  const saveCompletedSteps = (steps) => {
    try {
      localStorage.setItem(`completedSteps_module_${moduleId}`, JSON.stringify(steps));
    } catch (error) {
      console.error('Error saving completed steps:', error);
    }
  };

  const loadChecklistItems = () => {
    try {
      const saved = localStorage.getItem(`checklistItems_module_${moduleId}`);
      if (saved) {
        const checklistObj = JSON.parse(saved);
        setChecklistItems(checklistObj);
      }
    } catch (error) {
      console.error('Error loading checklist items:', error);
    }
  };

  const saveChecklistItems = (items) => {
    try {
      localStorage.setItem(`checklistItems_module_${moduleId}`, JSON.stringify(items));
    } catch (error) {
      console.error('Error saving checklist items:', error);
    }
  };

  const fetchLearningMaterials = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'learning_materials'));
      const materialsMap = new Map();
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        materialsMap.set(doc.id, {
          id: doc.id,
          title: data.title || 'Untitled Material',
          description: data.description || '',
          type: data.type || 'material',
          fileUrl: data.fileUrl || null,
          fileName: data.fileName || null,
          fileType: data.fileType || null,
          category: data.category || null
        });
      });
      setLearningMaterials(materialsMap);
      setMaterialsLoaded(true);
    } catch (error) {
      console.error('Error fetching learning materials:', error);
      setMaterialsLoaded(true); // Set to true even on error to prevent blocking
    }
  };

  const fetchModuleData = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'learning_modules'));
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.order === moduleId || data.id === moduleId) {
          setFetchedModuleData({
            id: data.order || moduleId,
            title: data.title,
            description: data.description,
            subsections: data.subsections || [],
            checklist: data.checklist || []
          });
        }
      });
    } catch (error) {
      console.error('Error fetching module data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get item details based on type and ID
  const getItemDetails = (item) => {
    if (item.type === 'material') {
      const material = learningMaterials.get(item.id);
      if (material) {
        return {
          title: material.title,
          description: material.description,
          type: 'material',
          fileUrl: material.fileUrl,
          fileType: material.fileType
        };
      }
      return {
        title: 'Material nicht gefunden',
        description: 'Das Lernmaterial konnte nicht gefunden werden',
        type: 'material',
        fileUrl: null,
        fileType: null
      };
    } else if (item.type === 'game') {
      // Handle game types - you might want to customize these based on the game ID
      const gameTypes = {
        'memory': { title: 'Zwillingspaare finden', description: 'Trainiere dein Gedächtnis mit dem Memory-Spiel' },
        'story': { title: 'Geschichte Spiel', description: 'Erzähle und höre Geschichten' },
        'wordmatch': { title: 'Wörter verbinden', description: 'Verbinde passende Wörter miteinander' },
        'connect': { title: 'Wörter verbinden', description: 'Verbinde passende Wörter miteinander' },
        'suchbilder': { title: 'Sprechhelden auf Lautsuche', description: 'Finde versteckte Objekte in den Bildern' },
        'search': { title: 'Sprechhelden auf Lautsuche', description: 'Finde versteckte Objekte in den Bildern' },
        'minimalpair': { title: 'Laut-Detektive', description: 'Unterscheide ähnliche Laute' },
        'minimal': { title: 'Laut-Detektive', description: 'Unterscheide ähnliche Laute' }
      };

      const gameInfo = gameTypes[item.id] || {
        title: `${item.id} Spiel`,
        description: `Spiele das ${item.id} Spiel`
      };

      return {
        title: gameInfo.title,
        description: gameInfo.description,
        type: 'game'
      };
    } else if (item.type === 'category') {
      // Handle category type - redirects to LearningMaterials page
      return {
        title: `Materialien: ${item.id}`,
        description: `Öffne die Kategorie ${item.id} in den Lernmaterialien`,
        type: 'category'
      };
    }

    return {
      title: `Unbekannter Typ: ${item.type}`,
      description: `Item ID: ${item.id}`,
      type: item.type || 'unknown'
    };
  };

  // Transform Firebase module data to expected structure
  const transformModuleData = (firebaseModule) => {
    if (!firebaseModule) return null;

    return {
      id: firebaseModule.id,
      order: firebaseModule.order,
      title: firebaseModule.title,
      description: firebaseModule.description,
      checklist: firebaseModule.checklist ? firebaseModule.checklist.map(item => item.question) : [],
      lessons: firebaseModule.subsections ? firebaseModule.subsections
        .sort((a, b) => a.order - b.order)
        .map((subsection, index) => ({
          id: subsection.id || `${firebaseModule.id}-${index + 1}`,
          title: subsection.title,
          steps: subsection.items ? subsection.items
            .sort((a, b) => a.order - b.order)
            .map((item, stepIndex) => {
              const details = getItemDetails(item);
              return {
                id: `${subsection.id || `${firebaseModule.id}-${index + 1}`}-${stepIndex + 1}`,
                type: details.type,
                title: details.title,
                description: details.description,
                originalId: item.id,
                duration: item.duration || '10 Min',
                fileUrl: details.fileUrl || null,
                fileType: details.fileType || null
              };
            })
            .filter((step) => {
              // Filter out materials that were not found
              if (step.type === 'material' && step.title === 'Material nicht gefunden') {
                return false;
              }
              return true;
            }) : []
        })) : []
    };
  };

  // Use provided moduleData or fetched data
  const activeModuleData = moduleData || fetchedModuleData;
  // Only transform module data when materials are loaded
  const currentModule = materialsLoaded ? transformModuleData(activeModuleData) : null;

  // Handle loading state
  if (loading || !materialsLoaded) {
    return (
      <Container>
        <div className="text-center my-5">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h3>Modul wird geladen...</h3>
          <p className="text-muted">
            {!materialsLoaded ? 'Lernmaterialien werden geladen...' : 'Die Moduldaten werden von Firebase geladen.'}
          </p>
        </div>
      </Container>
    );
  }

  // Handle case where no module data is available
  if (!currentModule) {
    return (
      <Container>
        <div className="text-center my-5">
          <h3>Modul nicht verfügbar</h3>
          <p className="text-muted">Die Moduldaten konnten nicht geladen werden.</p>
          <Button variant="primary" onClick={onBack}>
            Zurück zu allen Modulen
          </Button>
        </div>
      </Container>
    );
  }

  const isStepCompleted = (stepId) => completedSteps.includes(stepId);

  const isLessonCompleted = (lesson) => {
    return lesson.steps.every(step => isStepCompleted(step.id));
  };

  const isStepAccessible = (lesson, stepIndex) => {
    if (stepIndex === 0) return true; // First step is always accessible
    
    // Check if previous step is completed
    const previousStep = lesson.steps[stepIndex - 1];
    return isStepCompleted(previousStep.id);
  };

  const isSubsectionCompleted = (lesson) => {
    if (!lesson.steps || lesson.steps.length === 0) {
      // Check if the pseudo-step for empty sections is completed
      const pseudoStepId = `${lesson.id}-completed`;
      return isStepCompleted(pseudoStepId);
    }
    return lesson.steps.every(step => isStepCompleted(step.id));
  };

  const isSubsectionAccessible = (lessonIndex) => {
    // First subsection is always accessible
    if (lessonIndex === 0) return true;
    
    // Check if previous subsection is completed
    const previousLesson = currentModule.lessons[lessonIndex - 1];
    return previousLesson ? isSubsectionCompleted(previousLesson) : false;
  };

  const handleStepAction = (step, lesson, stepIndex) => {
    if (!isStepAccessible(lesson, stepIndex)) {
      alert('Bitte schließe zuerst die vorherigen Schritte ab.');
      return;
    }

    setCurrentStep(step.id);

    // Handle different actions based on step type
    switch (step.type) {
      case 'material':
        if (step.fileUrl) {
          // Directly open the file in a new tab
          window.open(step.fileUrl, '_blank');
          // Don't mark as completed automatically - user should do this manually after viewing
        } else {
          // No file URL available
          alert(`Datei für "${step.title}" konnte nicht gefunden werden.`);
          console.error('No fileUrl for material:', step.originalId);
        }
        break;
      case 'game':
        if (onNavigateToGame) {
          onNavigateToGame(step.originalId);
          // Mark step as completed after starting game
          markStepCompleted(step.id);
        } else {
          alert(`Game "${step.title}" würde jetzt gestartet werden.`);
        }
        break;
      case 'category':
        if (onNavigateToMaterials) {
          onNavigateToMaterials(step.originalId);
          // Mark step as completed after navigating to materials
          markStepCompleted(step.id);
        } else {
          alert(`Kategorie "${step.title}" würde jetzt geöffnet werden.`);
        }
        break;
      case 'video':
        // In real app, open video player
        break;
      case 'worksheet':
        // In real app, download PDF
        break;
      case 'task':
        // In real app, navigate to task page
        break;
      case 'explanation':
        // In real app, show explanation modal
        break;
      default:
        break;
    }
  };


  const markStepCompleted = async (stepId) => {
    if (!isStepCompleted(stepId)) {
      const updatedSteps = [...completedSteps, stepId];
      setCompletedSteps(updatedSteps);
      
      // Save to localStorage immediately for UI responsiveness
      saveCompletedSteps(updatedSteps);
      
      // Check if this step completion completes a section
      const completedSectionId = checkSectionCompletion(stepId, updatedSteps);
      
      // Save to Firebase
      const user = auth.currentUser;
      if (user) {
        await saveStepCompletion(user.uid, moduleId, stepId, completedSectionId);
      }
    }
  };

  const checkSectionCompletion = (completedStepId, allCompletedSteps) => {
    if (!currentModule.lessons) return null;
    
    // Find which section contains this step
    for (let lessonIndex = 0; lessonIndex < currentModule.lessons.length; lessonIndex++) {
      const lesson = currentModule.lessons[lessonIndex];
      
      // Check if this is a pseudo-step for an empty section
      const pseudoStepId = `${lesson.id}-completed`;
      if (completedStepId === pseudoStepId) {
        return lesson.id;
      }
      
      if (lesson.steps && lesson.steps.some(step => step.id === completedStepId)) {
        // Check if all steps in this section are now completed
        const allStepsInSection = lesson.steps.map(step => step.id);
        const allStepsCompleted = allStepsInSection.every(stepId => allCompletedSteps.includes(stepId));
        
        if (allStepsCompleted) {
          return lesson.id; // Return the section ID
        }
        break;
      }
    }
    
    return null;
  };

  const markStepUncompleted = async (stepId) => {
    if (isStepCompleted(stepId)) {
      const updatedSteps = completedSteps.filter(id => id !== stepId);
      setCompletedSteps(updatedSteps);
      
      // Save to localStorage immediately for UI responsiveness
      saveCompletedSteps(updatedSteps);
      
      // Re-evaluate section completion after step removal
      const updatedSections = reEvaluateSectionCompletion(updatedSteps);
      
      // Remove from Firebase
      const user = auth.currentUser;
      if (user) {
        await removeStepCompletion(user.uid, moduleId, stepId);
        
        // Update sections in Firebase if they changed
        if (updatedSections.length !== getCurrentCompletedSections().length) {
          await updateModuleSections(user.uid, moduleId, updatedSections);
        }
      }
    }
  };

  const reEvaluateSectionCompletion = (allCompletedSteps) => {
    if (!currentModule.lessons) return [];
    
    const completedSections = [];
    
    currentModule.lessons.forEach((lesson) => {
      if (!lesson.steps || lesson.steps.length === 0) {
        // Check if pseudo-step for empty section is completed
        const pseudoStepId = `${lesson.id}-completed`;
        if (allCompletedSteps.includes(pseudoStepId)) {
          completedSections.push(lesson.id);
        }
      } else {
        const allStepsInSection = lesson.steps.map(step => step.id);
        const allStepsCompleted = allStepsInSection.every(stepId => allCompletedSteps.includes(stepId));
        
        if (allStepsCompleted) {
          completedSections.push(lesson.id);
        }
      }
    });
    
    return completedSections;
  };

  const getCurrentCompletedSections = () => {
    // This would need to be tracked in state, but for now we'll re-evaluate
    return reEvaluateSectionCompletion(completedSteps);
  };

  const updateModuleSections = async (userId, moduleId, sections) => {
    try {
      const progressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
      const progressDoc = await getDoc(progressRef);
      
      if (progressDoc.exists()) {
        const currentProgress = progressDoc.data();
        const moduleProgress = currentProgress.modules || {};
        const moduleData = moduleProgress[moduleId] || { steps: [], sections: [] };
        
        moduleData.sections = sections;
        moduleProgress[moduleId] = moduleData;
        
        await updateDoc(progressRef, {
          modules: moduleProgress,
          updatedAt: new Date()
        });
      }
    } catch (error) {
      console.error('Error updating module sections:', error);
    }
  };

  const toggleStepCompletion = (stepId) => {
    if (isStepCompleted(stepId)) {
      markStepUncompleted(stepId);
    } else {
      markStepCompleted(stepId);
    }
  };

  const calculateModuleProgress = () => {
    if (!currentModule.lessons || currentModule.lessons.length === 0) return 0;
    
    const totalSteps = currentModule.lessons.reduce((acc, lesson) => 
      acc + (lesson.steps ? lesson.steps.length : 0), 0
    );
    
    if (totalSteps === 0) return 0;
    
    const completedInModule = currentModule.lessons.reduce((acc, lesson) => 
      acc + (lesson.steps ? lesson.steps.filter(step => isStepCompleted(step.id)).length : 0), 0
    );
    return (completedInModule / totalSteps) * 100;
  };

  const typeColors = {
    explanation: '#B2D8D8',
    video: '#FFB7B2',
    task: '#FFF3CD',
    worksheet: '#C7E9C7',
    material: '#2E86DE',
    game: '#E8DAF6',
    category: '#FFE4B5',
    unknown: '#F5F5F5'
  };

  // Add CSS styles for subsection locking
  const subsectionStyles = `
    .lesson-locked {
      opacity: 0.6;
      background-color: #f8f9fa;
    }
    
    .lesson-number.locked {
      background-color: #6c757d;
      color: white;
    }
    
    .lesson-number.completed {
      background-color: #28a745;
      color: white;
    }
    
    .lesson-number {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      background-color: #007bff;
      color: white;
    }
  `;

  return (
    <Container>
      <style dangerouslySetInnerHTML={{ __html: subsectionStyles }} />
      <div className="mb-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Button variant="link" className="p-0 text-decoration-none" onClick={onBack}>
            <Icon name="arrow-left" className="me-2" />Zurück zu allen Modulen
          </Button>
        </div>
        <h2 className="mb-2">{currentModule.title}</h2>
        <p className="text-secondary mb-4">{currentModule.description}</p>
      </div>

      <Row className="g-4">
        {currentModule.lessons && currentModule.lessons.length > 0 ? (
          currentModule.lessons.map((lesson, lessonIndex) => {
            const subsectionAccessible = isSubsectionAccessible(lessonIndex);
            const subsectionCompleted = isSubsectionCompleted(lesson);
            
            return (
            <Col key={lesson.id} lg={12}>
              <Card className={`lesson-card ${!subsectionAccessible ? 'lesson-locked' : ''}`}>
                <Card.Body>
                  <div className="d-flex align-items-center mb-4">
                    <div className={`lesson-number ${subsectionCompleted ? 'completed' : !subsectionAccessible ? 'locked' : ''}`}>
                      {!subsectionAccessible ? (
                        <Icon name="lock" size={16} />
                      ) : (
                        lessonIndex + 1
                      )}
                    </div>
                    <div className="ms-3">
                      <h4 className="mb-0">{lesson.title}</h4>
                      {!subsectionAccessible && (
                        <small className="text-muted">
                          Schließe zuerst {lessonIndex === 0 ? 'diese' : 'die vorherige'} Sektion ab
                        </small>
                      )}
                    </div>
                  </div>

                <div className="steps-container">
                  {lesson.steps && lesson.steps.length > 0 ? lesson.steps.map((step, stepIndex) => {
                    const accessible = subsectionAccessible && isStepAccessible(lesson, stepIndex);
                    const completed = isStepCompleted(step.id);
                    
                    return (
                      <div 
                        key={step.id} 
                        className={`step-item ${completed ? 'completed' : ''} ${!accessible ? 'step-locked' : ''}`}
                      >
                        <div className="step-connector" />
                        <div className="step-content">
                          <div 
                            className="step-icon"
                            style={{ 
                              backgroundColor: accessible ? `${typeColors[step.type]}20` : '#F0F0F0', 
                              color: accessible ? typeColors[step.type] : '#999' 
                            }}
                          >
                            {accessible ? (
                              <Icon name={
                                step.type === 'material' ? (
                                  step.fileType && step.fileType.startsWith('video/') ? 'video' :
                                  step.fileType && step.fileType.includes('pdf') ? 'document' :
                                  'document'
                                ) :
                                step.type === 'game' ? 'games' :
                                step.type === 'category' ? 'materials' :
                                step.type === 'video' ? 'video' :
                                step.type === 'explanation' ? 'explanation' :
                                step.type === 'task' ? 'edit' :
                                step.type === 'worksheet' ? 'document' :
                                'circle'
                              } size={20} />
                            ) : (
                              <Icon name="lock" size={20} />
                            )}
                          </div>
                          <div className="step-info">
                            <h6 className="mb-1">{step.title}</h6>
                            {step.description && (
                              <p className="text-muted small mb-2">{step.description}</p>
                            )}
                            <div className="d-flex align-items-center gap-3">
                              <small className="text-muted">Schritt {stepIndex + 1}</small>
                              <small className="text-muted">
                                <Icon name={
                                  step.type === 'material' ? 'document' :
                                  step.type === 'game' ? 'games' :
                                  step.type === 'category' ? 'materials' :
                                  'circle'
                                } size={16} className="me-1" />
                              </small>
                              {step.duration && (
                                <small className="text-muted">
                                  <Icon name="clock" size={14} className="me-1" />
                                  {step.duration}
                                </small>
                              )}
                            </div>
                          </div>
                          <div className="step-actions">
                            {accessible && !completed && (
                              <>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleStepAction(step, lesson, stepIndex)}
                                  className="me-2"
                                >
                                  {step.type === 'material' && (
                                    <>
                                      {step.fileType && step.fileType.startsWith('video/') ? 'Ansehen' : 'Öffnen'}
                                    </>
                                  )}
                                  {step.type === 'game' && (
                                    <>Spielen</>
                                  )}
                                  {step.type === 'category' && (
                                    <><Icon name="materials" size={14} className="me-1" />Materialien ansehen</>
                                  )}
                                  {step.type === 'video' && (
                                    <><Icon name="play" size={14} className="me-1" />Ansehen</>
                                  )}
                                  {step.type === 'worksheet' && (
                                    <><Icon name="download" size={14} className="me-1" />Herunterladen</>
                                  )}
                                  {step.type === 'task' && (
                                    <><Icon name="edit-3" size={14} className="me-1" />Starten</>
                                  )}
                                  {step.type === 'explanation' && (
                                    <><Icon name="book-open" size={14} className="me-1" />Lesen</>
                                  )}
                                  {!['material', 'game', 'category', 'video', 'worksheet', 'task', 'explanation'].includes(step.type) && (
                                    <><Icon name="circle" size={14} className="me-1" />Starten</>
                                  )}
                                </Button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => markStepCompleted(step.id)}
                                >
                                  <Icon name="check" size={14} className="me-1" />Abschließen
                                </Button>
                              </>
                            )}
                            {completed && (
                              <div 
                                className="step-check step-check-clickable" 
                                onClick={() => markStepUncompleted(step.id)}
                                style={{ cursor: 'pointer' }}
                                title="Klicken um Abschluss zu widerrufen"
                              >
                                Abgeschlossen
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="text-center py-4">
                      <p className="text-muted mb-3">
                        Keine Schritte für diese Lektion verfügbar.
                      </p>
                      {subsectionAccessible && !subsectionCompleted && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => {
                            // Mark the subsection as completed by creating a pseudo-step
                            const pseudoStepId = `${lesson.id}-completed`;
                            markStepCompleted(pseudoStepId);
                          }}
                        >
                          <Icon name="check" size={14} className="me-1" />Abschließen
                        </Button>
                      )}
                      {subsectionCompleted && (
                        <div className="step-check">
                          Abgeschlossen
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
          );
        })
        ) : (
          <Col lg={12}>
            <Card className="text-center py-5">
              <Card.Body>
                <h5 className="text-muted mb-3">Keine Lektionen verfügbar</h5>
                <p className="text-muted mb-0">
                  Für dieses Modul sind noch keine Lektionen konfiguriert.
                </p>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Module completion checklist */}
      {currentModule.checklist && currentModule.checklist.length > 0 && (
        <Row className="mt-5">
          <Col lg={12}>
            <Card className="checklist-card">
              <Card.Body>
                <div className="d-flex align-items-center mb-4">
                  <div className="checklist-icon">
                    <Icon name="checklist" size={24} />
                  </div>
                  <div className="ms-3">
                    <h4 className="mb-0">Abschluss-Checkliste</h4>
                    <p className="text-muted mb-0">
                      Überprüfe, was dein Kind in diesem Modul gelernt hat
                    </p>
                  </div>
                </div>

                <div className="checklist-items">
                  {currentModule.checklist.map((item, index) => {
                    const checklistKey = `${moduleId}-checklist-${index}`;
                    return (
                      <div key={index} className="checklist-item">
                        <Form.Check
                          type="checkbox"
                          id={checklistKey}
                          label={item}
                          checked={checklistItems[checklistKey] || false}
                          onChange={(e) => {
                            const updatedChecklistItems = {
                              ...checklistItems,
                              [checklistKey]: e.target.checked
                            };
                            setChecklistItems(updatedChecklistItems);
                            saveChecklistItems(updatedChecklistItems);
                          }}
                          className="checklist-checkbox"
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <small className="text-muted">
                        {Object.values(checklistItems).filter(v => v).length} von {currentModule.checklist.length} Fähigkeiten gemeistert
                      </small>
                    </div>
                    {Object.values(checklistItems).filter(v => v).length === currentModule.checklist.length && (
                      <Badge bg="success" className="px-3 py-2">
                        <Icon name="trophy" size={16} className="me-1" />
                        Modul abgeschlossen!
                      </Badge>
                    )}
                  </div>
                  {Object.values(checklistItems).filter(v => v).length === currentModule.checklist.length && (
                    <Button 
                      variant="success" 
                      className="w-100 mt-3"
                      onClick={async () => {
                        if (onModuleComplete && currentModule.order !== undefined) {
                          // Call the completion handler and wait for it
                          await onModuleComplete(currentModule.order);
                          
                          // Navigate back to module overview to see the unlocked module
                          if (onBack) {
                            onBack();
                          }
                          
                          // Navigate to next module if function is provided (optional)
                          if (onNavigateToNextModule) {
                            onNavigateToNextModule(currentModule.order);
                          }
                        }
                      }}
                    >
                      <Icon name="unlock" size={16} className="me-2" />
                      Nächstes Modul freischalten
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}

export default ModuleView;